import {getId, getType} from 'apis/model/base'
import {FollowButton} from 'components/Feed/FollowButton'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import numeral from 'numeral'
import React, {useContext, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import getTitle from "../../../helpers/getTitle";
import {FaWandMagicSparkles} from "react-icons/fa6";
import getLinkToDetail from 'helpers/getLinkToDetail'
import {bindQueryParams} from "../../AppLocals/functions/routerHelper";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import useAsyncAction from "../../../modules/asyncCache/useAsyncAction";
import {formatSimplifyDate} from "../../../helpers/dateUtils";
import {feed_fetchFeedByContainer_Api} from "../../../apis";
import {GoDotFill} from "react-icons/go";
import {RiSignalTowerFill} from "react-icons/ri";
import classNames from "classnames";
import {PulseBox, PulseLine} from "../../../components/EmptyHolder/helpers";
import {formatNumber} from "../../../helpers/number";


const FeaturedSkeleton = () => (
    <div className="flex flex-col gap-3 p-3">
        <div className="flex gap-4">
            <PulseBox width={96} height={80} borderRadius={8}/>
            <div className="flex flex-col flex-1 gap-2 pt-1">
                <PulseLine width="90%" height={16}/>
                <PulseLine width="60%" height={12}/>
                <PulseLine width="40%" height={12}/>
            </div>
        </div>
    </div>
)

const ArticleLink = ({item, title, commentCount, viewCount}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col leading-relaxed max-lines-3">
            <Link to={getLinkToDetail(item)} className="flex items-center text-gray-700 no-underline max-lines-3">
                <div className="flex items-start gap-1">
                    <GoDotFill size={10} className="text-color-500 flex-shrink-0 mt-1"/>
                    <span className="font-medium leading-snug">
                        {_.truncate(title, {length: 120})}
                    </span>
                </div>
            </Link>
            <div className="ml-4 flex items-center gap-1 text-2xs italic leading-snug">
                {commentCount > 0 && (
                    <div className="flex items-center gap-1 tracking-wider">
                        <span className="font-medium text-color-100">{formatNumber(commentCount)}</span>
                        <span className="text-slate-400 lowercase">
                            {t('comment')}
                        </span>
                    </div>
                )}
                {viewCount > 0 && (
                    <div className="flex items-center gap-1 tracking-wider">
                        {commentCount > 0 && <GoDotFill size={8} className="text-gray-500"/>}
                        <span className="font-medium text-color-100">{formatNumber(viewCount)}</span>
                        <span className="text-slate-400 lowercase">
                            {t('views')}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
};

const ChannelItem = ({item, style}) => {
    const t = useTranslate()
    const type = getType(item);
    const id = getId(item);
    const {isLg} = useContext(LayoutContext)
    const [data, setData] = useState([])

    const {handleAsyncAction, success} = useAsyncAction({
        apiInfo: feed_fetchFeedByContainer_Api,
        query: bindQueryParams([{id: id}, {prop: type}]),
        onSuccess: (result, data) => {
            const _data = _.get(data, 'response.data.data', [])
            setData(_data)
        },
    })
    useEffect(() => {
        handleAsyncAction()
    }, [])

    const featured = _.first(data);
    const secondaryLinks = _.tail(data);
    const showFeaturedArea = !success || !!featured

    if (!item) return null

    return (
        <div style={style}
             className="background hover:shadow-md hover:scale-2xs rounded-xl relative">
            <div
                className={classNames(
                    "border-b background-200 p-3",
                    showFeaturedArea ? 'mb-4 pb-2 rounded-t-xl' : 'rounded-xl'
                )}>
                <h2 className="flex items-center gap-2">
                    <RiSignalTowerFill className="flex-shrink-0" />
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-base text-color-000 tracking-wide no-underline truncate">
                        {getTitle(item)}
                    </Link>
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {item.total_followers > 0 && (
                        <span className="flex items-baseline gap-1 text-xs text-color-300 tracking-wider lowercase whitespace-nowrap">
                            <strong className="text-sm text-blue-500 tabular-nums">{numeral(item.total_followers).format()}</strong>
                            {t('followers')}
                        </span>
                    )}
                    {!!_.get(item, 'selected_by_editor') && (
                        <span className="h-6 px-2 py-1 flex flex-center gap-1 flex-shrink-0 whitespace-nowrap font-medium text-xs text-blue-600 bg-blue-100 tracking-wide rounded-full">
                            <FaWandMagicSparkles size={10}/>
                            {!!isLg && t("editor's choice")}
                        </span>
                    )}
                    <div className="flex-1"/>
                    <FollowButton
                        size="small"
                        item={item}
                    />
                </div>
            </div>
            {!success && <FeaturedSkeleton/>}
            {success && !!featured && (
                <div className="flex flex-col gap-3 p-3">
                    {/* Featured Section based on Image 2 */}
                    <div className="flex gap-4">
                        {!!featured?.content?.overview?.photo && (
                            <div className="w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100">
                                <img
                                    src={featured?.content?.overview?.photo}
                                    alt={featured?.content?.title || featured?.content?.overview?.title || featured?.content?.overview?.description}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                            <Link to={getLinkToDetail(featured?.content)}
                                  className="max-lines-2 font-bold text-lg text-gray-800 no-underline">
                                <h3 className="leading-tight">
                                    {featured?.content?.title || featured?.content?.overview?.title || featured?.content?.overview?.description}
                                </h3>
                            </Link>
                            <div className="space-x-2 text-xs tracking-wide">
                            <span
                                className="text-color-300 italic">{formatSimplifyDate(featured?.content?.created, 'fullTime')}</span>
                                <GoDotFill size={8} className="text-color-500"/>
                                <span className="font-semibold text-gray-600 italic">
                                    {getTitle(featured?.content?.owner)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Secondary Links based on Image 1 & 2 */}
                    {secondaryLinks?.length > 0 && (
                        <div className="py-2 flex flex-col space-y-3">
                            {secondaryLinks.map((link, idx) => {
                                return (
                                    <ArticleLink key={idx}
                                                 item={link?.content}
                                                 title={link?.content?.title || link?.content?.overview?.title || link?.content?.overview?.description}
                                                 commentCount={link?.content?.total_comments} viewCount={link?.content?.total_views}/>
                                )
                            })}
                        </div>
                    )}

                    {/* Footer / See More */}
                    <div className="mt-4 flex justify-end">
                        <Link to={getLinkToDetail(item)}
                              className="space-x-1 font-semibold text-xs text-secondary-600 hover:text-blue-600 tracking-wide no-underline">
                            {`${t("go to channel")}: `} <label className="italic">{getTitle(item)}</label>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}

export const renderChannelItem = (item) => (
    <ChannelItem
        item={item}
        key={getId(item)}
    />
)

// Bản gợi ý gọn cho block "Gợi ý" (SuggestedItems.js) - KHÔNG gọi feed_fetchFeedByContainer_Api lấy bài
// mẫu như ChannelItem (N+1 request, nặng UI cho 1 widget phụ). Theo pattern "Card/list item nhiều
// action - 2 hàng" đã chuẩn hoá (docs/ui/design-principles.md): hàng 1 = icon/title/meta, hàng 2 =
// mô tả kênh + CTA Follow, để user hiểu kênh nói về gì trước khi bấm Theo dõi.
const ChannelSuggestItem = ({item, style}) => {
    const t = useTranslate()
    if (!item) return null

    const {total_followers, total_posts, total_articles, selected_by_editor, description} = item
    const postsCount = Number(total_posts || 0) + Number(total_articles || 0)

    return (
        <div
            style={style}
            className="flex flex-col background border border-color-50 hover:shadow-sm rounded-xl overflow-hidden transition-all duration-300">
            {/* Hàng 1: icon + title + meta */}
            <div className="flex items-start gap-3 p-3 pb-2">
                <div style={{width: 40, height: 40, flexShrink: 0}} className="rounded-lg flex flex-center bg-blue-50">
                    <RiSignalTowerFill size={18} className="text-blue-600"/>
                </div>
                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-sm text-color-000 tracking-wide no-underline max-lines-1">
                        {getTitle(item)}
                    </Link>
                    <div className="flex items-center gap-2 flex-wrap text-2xs text-color-300">
                        {total_followers > 0 && (
                            <span className="flex items-baseline gap-1 tracking-wider lowercase whitespace-nowrap">
                                <strong className="text-xs text-blue-500 tabular-nums">{formatNumber(total_followers)}</strong>
                                {t('followers')}
                            </span>
                        )}
                        {postsCount > 0 && (
                            <span className="flex items-baseline gap-1 tracking-wider lowercase whitespace-nowrap">
                                {total_followers > 0 && <GoDotFill size={6} className="text-color-500"/>}
                                <strong className="text-xs text-blue-500 tabular-nums">{formatNumber(postsCount)}</strong>
                                {t('news post').toLowerCase()}
                            </span>
                        )}
                        {!!selected_by_editor && (
                            <span className="flex items-center gap-1 font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 leading-none">
                                <FaWandMagicSparkles size={9}/>
                                {t("editor's choice")}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {/* Hàng 2: mô tả kênh + CTA Follow */}
            <div className="flex items-center gap-3 px-3 pb-3">
                {description ? (
                    <p className="flex-1 min-w-0 m-0 text-xs text-color-300 tracking-wide max-lines-2">
                        {description}
                    </p>
                ) : (
                    <div className="flex-1"/>
                )}
                <div className="flex-shrink-0">
                    <FollowButton size="small" item={item}/>
                </div>
            </div>
        </div>
    )
}

export const renderChannelSuggestItem = (item) => (
    <ChannelSuggestItem
        item={item}
        key={getId(item)}
    />
)


const ChannelItemList = ({item, index, style}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)

    if (!item) return null

    return (
        <div style={style} className="p-4 transition-all duration-300 relative list-item">
            <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600 flex flex-center">
                    <RiSignalTowerFill size={20} />
                </div>
                <div className="flex flex-col flex-1">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-base text-color-000 tracking-wide no-underline">
                        <h2>{getTitle(item)}</h2>
                    </Link>
                    <div className="flex items-center gap-3 flex-wrap">
                        {item.total_followers > 0 && (
                            <span className="flex items-baseline gap-1 text-xs text-color-300 tracking-wider lowercase whitespace-nowrap">
                                <strong className="text-sm text-blue-500 tabular-nums">{Number(item.total_followers).toLocaleString()}</strong>
                                {t('followers')}
                            </span>
                        )}
                        {(item.total_posts > 0 || item.total_articles > 0) && (
                            <span className="flex items-baseline gap-1 text-xs text-color-300 tracking-wider lowercase whitespace-nowrap">
                                <strong className="text-sm text-blue-500 tabular-nums">{Number(item.total_posts + item.total_articles).toLocaleString()}</strong>
                                {t('news post').toLowerCase()}
                            </span>
                        )}
                        {!!_.get(item, 'selected_by_editor') && (
                            <span className="h-6 px-2 py-1 flex flex-center gap-1 flex-shrink-0 whitespace-nowrap font-medium text-xs text-blue-600 bg-blue-100 tracking-wide rounded-full">
                                <FaWandMagicSparkles size={10}/>
                                {!!isLg && t("editor's choice")}
                            </span>
                        )}
                    </div>
                    <div className="text-color-300 tracking-wider max-lines-3">
                        {item?.description}
                    </div>
                </div>
            </div>
        </div>
    )
}

export const renderChannelItemList = (item, index) => (
    <ChannelItemList
        item={item}
        key={getId(item)}
    />
)